/**
 * Shift worker-type sentinel for "NURSE" — a grouping that expands to both
 * LVN and RN qualifications when the shift is created, priced, or paid.
 */
export const NURSE_QUALIFICATION = "NURSE";

/** Per-qualification credential codes that `NURSE` expands into. */
export const NURSE_QUALIFICATIONS: string[] = ["LVN", "RN"];

/**
 * Shift worker-type sentinel for "CAREGIVER" — at the Roster-eligibility
 * layer this admits both the CAREGIVER credential itself AND CNA workers.
 * Note: the rest of the FE (charge/pay-rate UIs) treats CAREGIVER shifts
 * as single-rate; only `expandQualificationForRosterEligibility` below
 * does the umbrella expansion.
 */
export const CAREGIVER_QUALIFICATION = "CAREGIVER";

/** Per-qualification credential codes that `CAREGIVER` expands into for Roster eligibility. */
export const CAREGIVER_QUALIFICATIONS: string[] = ["CAREGIVER", "CNA"];

/**
 * Expands a shift's `workerType` / `agentReq` into the concrete qualification
 * codes that drive per-qualification pricing, pay, offer-estimate calls, and
 * dual-rate admin/facility shift UIs. Only NURSE is multi-qualification here;
 * CAREGIVER (and every other type) passes through as a single rate. Do NOT
 * extend this to umbrella CAREGIVER — downstream save-handlers, charge/pay
 * inputs, and offer-estimate fetchers all assume CAREGIVER → single rate.
 * For Roster eligibility matching use `expandQualificationForRosterEligibility`.
 *
 *   NURSE    → ["LVN", "RN"]
 *   anything → [self]
 */
export function getQualificationsForWorkerType(workerType: string): string[] {
  if (workerType === NURSE_QUALIFICATION) {
    return NURSE_QUALIFICATIONS;
  }
  return [workerType];
}

/**
 * Expands a shift's `workerType` into the worker credential codes that
 * satisfy it for **Roster eligibility / fanout** purposes. Mirrors the BE's
 * `expandRequiredQualifications` (`src/utils/resolve-worker-qualification-matches.ts`):
 *
 *   NURSE     → ["LVN", "RN"]
 *   CAREGIVER → ["CAREGIVER", "CNA"]
 *   anything  → [uppercased]
 *
 * Used only by Roster-side code (eligibility filtering on the post-shift
 * form). Charge/pay-rate UIs use `getQualificationsForWorkerType` instead,
 * which keeps CAREGIVER as a single-rate shift.
 */
export function expandQualificationForRosterEligibility(workerType: string): string[] {
  const upper = workerType?.toUpperCase();
  if (upper === NURSE_QUALIFICATION) {
    return NURSE_QUALIFICATIONS;
  }
  if (upper === CAREGIVER_QUALIFICATION) {
    return CAREGIVER_QUALIFICATIONS;
  }
  return [upper];
}
